import { Fragment } from "react";
import PropTypes from "prop-types";

// Layouts
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import TierItemsLayout from "@/Layouts/components/Tiers/TierItemsLayout";

// MUI
import { Collapse, Stack, Typography } from "@mui/material";

const getTooltip = (tooltips, key) => {
	if (!tooltips || !key) {
		return null;
	}
	return tooltips?.[key] || null;
};

function RenderTier(props) {
	const { content, tooltips, rows, errors, errorsMessages, readOnly, maxDoubleInputValue, onActionUpdate, onActionAdd, onActionDelete } =
		props;

	return (
		<TierItemsLayout
			minimumRows={2}
			columnSpacing={4}
			rows={rows}
			columns={[
				{
					_id: content?.left?._id,
					title: content?.left?.title || "",
					tooltip: getTooltip(tooltips, content?.left?._id),
					component: {
						type: "DoubleInput",
						order: content?.left?.useValues || [],
						disableCommonInputs: true,
						properties: {
							firstInput: {
								data: [
									{
										type: "Percent",
										min: 0,
										max: maxDoubleInputValue,
										decimalsEditable: true,
										decimals: 2,
										disabled: true,
									},
								],
								sx: { maxWidth: "75px" },
							},
							secondInput: {
								data: [
									{
										type: "Percent",
										min: 0,
										max: maxDoubleInputValue,
										decimalsEditable: true,
										decimals: 2,
									},
								],
								sx: { maxWidth: "75px" },
							},
						},
						callbacks: {
							onActionUpdate: (positions, area, value) => {
								onActionUpdate?.(positions, area, value);
							},
						},
					},
				},
				{
					_id: content?.right?._id,
					title: content?.right?.title || "",
					tooltip: getTooltip(tooltips, content?.right?._id),
					component: {
						type: "InputForm",
						order: content?.right?.useValues || [],
						properties: {
							input: {
								data: [
									{
										type: "Percent",
										min: -100,
										max: 100,
										decimalsEditable: true,
										decimals: 2,
									},
								],
								sx: { maxWidth: "95px" },
							},
						},
						callbacks: {
							onActionUpdate: (positions, area, value) => {
								onActionUpdate?.(positions, area, value);
							},
						},
					},
				},
			]}
			errors={errors}
			errorsMessages={errorsMessages}
			readOnly={readOnly}
			onActionAdd={onActionAdd}
			onActionDelete={onActionDelete}
		/>
	);
}
RenderTier.propTypes = {
	content: PropTypes.object,
	tooltips: PropTypes.object,
	rows: PropTypes.array,
	errors: PropTypes.object,
	errorsMessages: PropTypes.array,
	readOnly: PropTypes.bool,
	maxDoubleInputValue: PropTypes.number,
	onActionUpdate: PropTypes.func,
	onActionAdd: PropTypes.func,
	onActionDelete: PropTypes.func,
};

// Previously known as ReputationTierLayout
export default function ReputationTiers(props) {
	const {
		content = null,
		showContent = false,
		actions = [],
		showActions = true,
		showForgiveness = false,
		forgiveness = {
			input: "Unit",
			value: 0,
		},
		tooltips = null,
		rows = [],
		errors = null,
		errorsMessages = [],
		readOnly = false,
		maxDoubleInputValue = 100,
		sxContent = null,
		actionsJustifyContent = "flex-start",
		onActionUpdate = () => {},
		onActionAdd = () => {},
		onActionDelete = () => {},
	} = props;

	return (
		<Fragment>
			{Boolean(actions?.length && showActions) && (
				<Stack direction={"row"} spacing={22} sx={{ width: "100%", minWidth: "500px" }} justifyContent={actionsJustifyContent}>
					{actions}
				</Stack>
			)}

			{Boolean(content) && (
				<Collapse in={showContent} timeout="auto">
					<Stack direction={"row"} sx={sxContent}>
						<Stack>
							<RenderTier
								content={content}
								tooltips={tooltips}
								rows={rows}
								errors={errors}
								errorsMessages={errorsMessages}
								readOnly={readOnly}
								maxDoubleInputValue={maxDoubleInputValue}
								onActionUpdate={onActionUpdate}
								onActionAdd={onActionAdd}
								onActionDelete={onActionDelete}
							/>
						</Stack>
						<Stack sx={{ marginTop: "35px", marginLeft: "50px" }}>
							{showContent && showForgiveness && (
								<Stack>
									<Stack mb={"6px"} mt={"-30px"}>
										<Typography className="!font-montserrat !font-medium !text-[16px] text-default">
											Forgiveness
										</Typography>
									</Stack>

									<Stack direction={"row"} alignItems={"center"} sx={{ width: "100px" }}>
										<Typography
											sx={{
												fontWeight: 500,
												fontFamily: "'Montserrat', sans-serif",
												fontSize: "16px",
												color: "#666",
											}}
										>
											+/-
										</Typography>
										<InputFormExtension
											data={[
												{
													type: "Unit",
													min: -100,
													max: 100,
													decimalsEditable: true,
													decimals: 2,
													disabled: false,
												},
												{
													type: "Percent",
													min: -100,
													max: 100,
													decimalsEditable: true,
													decimals: 2,
													disabled: false,
												},
											]}
											value={forgiveness?.value || 0}
											defaultType={forgiveness?.input || "Unit"}
											disabled={readOnly}
											onChangeType={(_, value) => {
												onActionUpdate?.(null, "input", value);
											}}
											onActionUpdate={(value) => {
												onActionUpdate?.(null, "value", value);
											}}
											sx={{
												width: "75px",
												marginLeft: "10px",
											}}
										/>
									</Stack>
								</Stack>
							)}
						</Stack>
					</Stack>
				</Collapse>
			)}
		</Fragment>
	);
}
ReputationTiers.propTypes = {
	content: PropTypes.object,
	showContent: PropTypes.bool,
	actions: PropTypes.array,
	showActions: PropTypes.bool,
	showForgiveness: PropTypes.bool,
	forgiveness: PropTypes.object,
	rows: PropTypes.array,
	errors: PropTypes.object,
	errorsMessages: PropTypes.array,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionAdd: PropTypes.func,
	onActionDelete: PropTypes.func,
	sxContent: PropTypes.object,
	actionsJustifyContent: PropTypes.string,
	maxDoubleInputValue: PropTypes.number,
};
